import React from 'react';
import type { ScreenBackground } from '@/store/editorSlice';
import { resolveScreenLayout } from '@/components/screen-layout/resolveScreenLayout';
import type { ScreenOverlayTone } from '@/themes/stylePack.types';
import { useAdaptivePhotoScrim } from './useAdaptivePhotoScrim';
import { isLightImageBackground } from './screenBackgroundTone';
import { withRichColorAliases } from '@/types/rich-text';
import { quizChromeInsetVars } from '@/components/quiz-runtime/quizHeaderInset';

/**
 * Compute the CSS background shorthand for a scrim overlay type.
 * Returns a gradient string that ensures text is readable over any image.
 *
 * КАРТОЧНЫЙ режим (выкаченный пейволл) — трогать нельзя: значения ниже и есть
 * его сегодняшний вид. Полноэкранный режим собирает вуаль из переменных пака
 * отдельной веткой (fullScreenScrimBackground).
 */
function scrimGradient(overlay: ScreenBackground['overlay']): string {
  if (overlay === 'scrim-full') {
    return 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.55) 100%)';
  }
  if (overlay === 'none') {
    return 'none';
  }
  // scrim-bottom (default)
  return 'linear-gradient(to top, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0.20) 55%, rgba(0,0,0,0) 100%)';
}

/**
 * WEB-927: вуаль полноэкранного фона собирается из ПЕРЕМЕННЫХ стиль-пака
 * (--screen-overlay-direction / --screen-overlay-opacity, эмитит
 * stylePackToStyle) — чёрный цвет задаёт рендерер, пак цветов не эмитит.
 * Фолбэки в var() повторяют легаси-значения, поэтому воронка без пака выглядит
 * как раньше. Форма зависит от тона: ровная вуаль держит одну прозрачность на
 * обоих стопах, нижняя — гаснет в прозрачность.
 */
function fullScreenScrimBackground(tone: ScreenOverlayTone): string {
  if (tone === 'scrim-full') {
    return (
      'linear-gradient(var(--screen-overlay-direction, to bottom),' +
      ' rgba(0, 0, 0, var(--screen-overlay-opacity, 0.55)) 0%,' +
      ' rgba(0, 0, 0, var(--screen-overlay-opacity, 0.55)) 100%)'
    );
  }
  return (
    'linear-gradient(var(--screen-overlay-direction, to top),' +
    ' rgba(0, 0, 0, var(--screen-overlay-opacity, 0.7)) 0%,' +
    ' rgba(0, 0, 0, 0) 100%)'
  );
}

/** Переменные пака, которыми экран перебивает пак, когда задал тон САМ. */
function overlayOverrideVars(tone: ScreenOverlayTone): React.CSSProperties {
  return {
    '--screen-overlay-direction': tone === 'scrim-full' ? 'to bottom' : 'to top',
    '--screen-overlay-opacity': tone === 'scrim-full' ? '0.55' : '0.7',
  } as React.CSSProperties;
}

/**
 * WEB-872 / WEB-1805: прижатый светлый текст на тёмной поверхности. Один
 * литерал на два случая (вуаль над фото и тёмная заливка экрана) — вторая
 * копия тех же двух переменных разъехалась бы при первой же правке.
 *
 * WEB-2377 п.2: прижатие идёт ЧЕРЕЗ `withRichColorAliases` — вместе с
 * переменной темы перекрывается её двойник `--rich-*`. Двойник объявлен один
 * раз на КОРНЕ воронки, подстановка считается там же, поэтому без этого кусок
 * текста, покрашенный токеном палитры (а именно токен ставит серверный гард
 * контраста на экранах с вуалью), оставался тёмным на тёмном. Набор двойников
 * выводится из таблицы токенов, второй копии списка нет.
 */
const LIGHT_TEXT_PIN = withRichColorAliases({
  '--color-text-primary': '#ffffff',
  '--color-text-secondary': 'rgba(255,255,255,0.82)',
}) as React.CSSProperties;

/**
 * WEB-927: коробка контента полноэкранного фона. Признак full-screen снимает
 * поля СТРАНИЦЫ (:has() в public-paywall.css), поэтому поля и ширину читаемой
 * колонки даёт сам слой контента. WEB-1805 переиспользует её у заливки: экран,
 * залитый цветом, идёт edge-to-edge своим признаком (`data-bg-fill='edge'`,
 * ревизия С4) и без этой коробки остался бы без полей.
 */
export const FULL_SCREEN_CONTENT_BOX: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: 'var(--content-max-w, 680px)',
  marginLeft: 'auto',
  marginRight: 'auto',
  // WEB-973: контент отступает от выемок iPhone (viewport-fit=cover пускает
  // ФОН под них, а текст/кнопки — нет; на десктопе env()=0).
  // stylepack/bg-top-pad: верхнее поле — переменная с прежними 24px запасом.
  // Шаг квиза на телефоне ставит её в 0 (public-paywall.css, правило
  // WEB-2092 п.3) — экран с фоном начинается там же, где обычный. Не инлайн
  // по ширине окна: инлайн не перебить media-запросом (шрам WEB-782).
  paddingTop: 'calc(var(--screen-bg-content-pad-top, 24px) + env(safe-area-inset-top, 0px))',
  paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
  paddingLeft: 'var(--screen-pad-x, 16px)',
  paddingRight: 'var(--screen-pad-x, 16px)',
  // Свои отступы шапки квиза (stylepack/header-insets): поля вокруг шапки —
  // те же, что выше (верх — без выреза: вырез экраном не считается), в том
  // числе переменная верхнего поля stylepack/bg-top-pad: на телефоне она 0, и
  // жёсткие 24px подняли бы свою шапку на 24 − paddingTop (шов сведения).
  ...quizChromeInsetVars(
    'var(--screen-bg-content-pad-top, 24px)',
    'var(--screen-pad-x, 16px)',
  ),
};

/**
 * WEB-927 / WEB-1805: корень, который занимает ВЕСЬ экран. Базис ОБЯЗАН быть
 * auto, а не 0 (как давал `flex: 1`): в скроллящемся хосте — канвасе телефона
 * в конструкторе — элемент с базисом 0 занимает ровно видимую высоту и НЕ
 * растёт под контент (живой замер 2026-08-04: корень 455 при контенте 768), а
 * слои фона (absolute inset:0) кроются по корню — низ экрана оставался без
 * фона. minHeight:100% тут не спасал, он же и мешал: перебивал min-height:auto
 * флекс-элемента, которым тот растёт под содержимое. shrink:0 — чтобы нехватка
 * места не вернула ту же обрезку. Высота = max(контент, 100% хоста).
 */
const FULL_SCREEN_ROOT: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0 auto',
  minHeight: '100%',
};

/**
 * Сплошной ли фон экрана: заливка-цвет (hex/rgb/rgba), без `gradient(` и
 * `url(`. Только такой цвет может перенять прибитая шапка, не разойдясь с
 * видимым фоном.
 */
export function isSolidFill(fill: string | undefined): fill is string {
  return !!fill && !/gradient\(|url\(/i.test(fill);
}

interface ScreenBackgroundWrapperProps {
  background?: ScreenBackground;
  /**
   * WEB-927: id стиль-пака воронки — источник ДЕФОЛТА вуали в полноэкранном
   * режиме (в карточном дефолт остаётся легаси-'scrim-bottom', см. резолвер).
   */
  stylePackId?: string;
  /**
   * WEB-927: за какой режим отвечает ЭТА точка монтирования.
   *
   * Сегодня проп передаёт один вызывающий — квизовый визард
   * (QuizScreenWizard.tsx, `appliesTo="full-screen"`): его обёртка стоит вокруг
   * ВСЕГО экрана, и карточный фон рисовать ей нечем. Остальные точки
   * (PaywallScreenWizard, пост-оплатный экран паблика и его превью) монтируют
   * одну обёртку без пропа — она обслуживает оба режима, как и раньше.
   *
   * РЕВИЗИЯ WEB-1805 (М13): здесь стояло «квиз монтирует обёртку дважды —
   * вокруг экрана и вокруг потока блоков». Второй точки в квизе нет (грепом по
   * `<ScreenBackgroundWrapper` — четыре вызывающих, у квиза один), и описание
   * несуществующего дерева опаснее отсутствия описания: следующая правка
   * заливки исходила бы из него. Смысл пропа при этом прежний — точка
   * монтирования рисует только свой режим.
   */
  appliesTo?: 'card' | 'full-screen';
  children: React.ReactNode;
}

/**
 * Wraps screen content in a background image container with a contrast-safe
 * gradient scrim. Renders children directly when no background is present
 * (backward-compatible).
 */
export function ScreenBackgroundWrapper({
  background,
  stylePackId,
  appliesTo,
  children,
}: ScreenBackgroundWrapperProps): React.ReactElement {
  const layout = resolveScreenLayout({ background }, stylePackId);

  // WEB-1078 п.3: overlay:'none' поверх фото-фона больше не значит «текст
  // без защиты» — живой замер дал контраст 1.07-1.56 при норме 3:1. Решение
  // владельца продукта (09.08): вместо опт-аута — адаптивная вуаль, сила
  // которой подбирается по РЕАЛЬНОЙ яркости конкретной фотографии (несколько
  // точек — под разными частями текста фото разной яркости, useAdaptivePhotoScrim
  // сэмплирует, themes/adaptiveScrim.ts считает силу). Хук обязан звонить на
  // каждый рендер в ОДНОМ и том же порядке (правила хуков), поэтому вызван
  // здесь, до ранних return — `active` гасит сэмплирование, когда эта точка
  // монтирования всё равно не рисует фон (appliesTo не совпал с режимом).
  // Решение владельца №3: «светлая картинка» — ни вуали (явной или
  // адаптивной), ни светлого текста поверх (screenBackgroundTone.ts).
  const lightImage = isLightImageBackground(background);
  const needsAdaptiveScrim =
    layout.hasBackgroundImage && layout.overlay === 'none' && !lightImage;
  const adaptiveScrimActive =
    needsAdaptiveScrim && (!appliesTo || appliesTo === layout.mode);
  const adaptiveOpacity = useAdaptivePhotoScrim(
    adaptiveScrimActive ? background?.imageUrl : undefined,
    adaptiveScrimActive,
  );

  // WEB-1805: собственная заливка экрана (цвет/градиент). Заливка — свойство
  // ЭКРАНА, а не потока блоков, поэтому её рисует только экранная точка
  // монтирования: на карточной точке (появись она) цвет лёг бы вторым слоем и
  // покрасил карточку блоков вместо экрана.
  const paintsImage =
    layout.hasBackgroundImage && (!appliesTo || appliesTo === layout.mode);
  const paintsFill = !!layout.backgroundFill && (!appliesTo || appliesTo === 'full-screen');

  if (!paintsImage && !paintsFill) {
    return <>{children}</>;
  }

  const position = background?.position ?? 'center';
  const overlay = layout.overlay;
  const hasExplicitScrim = overlay !== 'none' && !lightImage;
  const hasScrim = hasExplicitScrim || needsAdaptiveScrim;
  const isFullScreen = layout.mode === 'full-screen';

  // WEB-1805: где именно лежит слой заливки.
  //  • есть ПОЛНОЭКРАННАЯ картинка — заливка ложится нижним слоем В ТОТ ЖЕ
  //    корень: отдельная обёртка дала бы вторую колонку полей, и контент
  //    отступил бы дважды;
  //  • картинки нет или она карточная — заливке нужен свой корень во весь
  //    экран: карточный корень скруглён и высотой по контенту, цвет в нём стал
  //    бы плашкой, а не фоном экрана.
  const fillInsideImageRoot = paintsFill && paintsImage && isFullScreen;
  const needsFillRoot = paintsFill && !fillInsideImageRoot;

  const fillLayer = (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: layout.backgroundFill,
        zIndex: 0,
      }}
      aria-hidden="true"
      data-testid="screen-bg-fill-layer"
      // Значение заливки продублировано атрибутом СОЗНАТЕЛЬНО: jsdom выбрасывает
      // из `style` всё, что не умеет разобрать (градиенты — целиком), поэтому
      // сторож рендера иначе не смог бы отличить «нарисовали правильно» от
      // «не нарисовали вовсе». Тот же приём, что у `data-overlay` рядом.
      data-fill={layout.backgroundFill}
    />
  );

  // WEB-872 п.3: a dark scrim sits between the background image and the
  // screen's content, but the content itself kept inheriting whatever text
  // color the funnel theme picked (often dark for light themes) — dark text
  // over a dark scrim reads as invisible. Pin readable light text on the
  // content layer only while a scrim is actually rendered. This also fixes
  // WEB-872 п.1's glass card: it reads --color-text-primary via CSS
  // inheritance, so it picks up this pin automatically without needing any
  // screen-background context passed down to it.
  //
  // WEB-1078 п.3: overlay:'none' used to skip this pin entirely — the
  // funnel's own (often dark) text color then sat directly on the photo with
  // nothing measuring it. hasScrim now also covers the adaptive path, so the
  // pin applies there too; the adaptive scrim's opacity is calibrated
  // specifically to keep THIS light text readable (useAdaptivePhotoScrim).
  // WEB-1805: тёмная ЗАЛИВКА держит текст ровно тем же способом, что вуаль
  // над фото, — иначе тёмный текст темы на тёмном экране читается как пустота.
  const pinsLightText =
    hasScrim || (fillInsideImageRoot && layout.fillIsDark);

  const contentStyle: React.CSSProperties = {
    position: 'relative',
    zIndex: 2,
    ...(pinsLightText ? LIGHT_TEXT_PIN : {}),
    // WEB-927: полноэкранный фон снимает поля со страницы (см. правила
    // :has() в public-paywall.css), поэтому дышать контенту даёт сам слой
    // контента — полями и шириной колонки из стиль-пака.
    ...(isFullScreen ? FULL_SCREEN_CONTENT_BOX : {}),
  };

  const rootStyle: React.CSSProperties = isFullScreen
    ? {
        // Хост (колонка страницы под правилом :has() или канвас телефона) —
        // flex-колонка: экран забирает всю её высоту, иначе картинка обрежется
        // по высоте контента и низ страницы останется пустым (WEB-627/1032,
        // подробности — у FULL_SCREEN_ROOT).
        ...FULL_SCREEN_ROOT,
        // Тон, заданный САМИМ экраном, перебивает переменные пака; тон,
        // унаследованный от пака, оставляет их как есть.
        ...(layout.overlayIsExplicit ? overlayOverrideVars(overlay) : {}),
      }
    : {
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        marginBottom: 16,
      };

  // Дерево картинки — ровно то, что рисовалось до WEB-1805. Экран без картинки
  // пропускает его целиком: рисовать нечего, а лишний корень поменял бы
  // раскладку выкаченных воронок.
  const imageTree = paintsImage ? (
    <div style={rootStyle} data-testid="screen-background-wrapper" data-bg-mode={layout.mode}>
      {/* WEB-1805: заливка экрана — САМЫЙ нижний слой, картинка ложится
          поверх неё (порядок слоёв css `background`). */}
      {fillInsideImageRoot && fillLayer}
      {/* Background image layer */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${background?.imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: position,
          zIndex: 0,
        }}
        aria-hidden="true"
        data-testid="screen-bg-image"
      />
      {/* Scrim layer */}
      {hasScrim && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: hasExplicitScrim
              ? isFullScreen
                ? fullScreenScrimBackground(overlay)
                : scrimGradient(overlay)
              : `rgba(0, 0, 0, ${adaptiveOpacity})`,
            zIndex: 1,
          }}
          aria-hidden="true"
          data-testid="screen-bg-scrim"
          data-overlay={hasExplicitScrim ? overlay : 'adaptive'}
        />
      )}
      {/* Content */}
      <div style={contentStyle} data-testid="screen-bg-content">
        {children}
      </div>
    </div>
  ) : (
    <>{children}</>
  );

  if (!needsFillRoot) return imageTree;

  // WEB-1805: собственный корень заливки. Признак `data-bg-fill='edge'` — не
  // косметика: им включаются правила :has() в public-paywall.css, снимающие
  // поля страницы и колонки. Без них цвет экрана лёг бы полосой шириной с
  // читаемую колонку, а «тёмный экран среди белых» так не собирается.
  //
  // РЕВИЗИЯ WEB-1805 (С4): признак СВОЙ, а не заимствованный
  // `data-bg-mode='full-screen'`. Заимствование ломало инвариант WEB-1108
  // («card и full-screen взаимоисключающие»): экран с КАРТОЧНОЙ картинкой и
  // заливкой давал дерево «снаружи full-screen, внутри card», и на ≥1024px
  // срабатывали ОБА набора десктопных правил — колонку сужало дважды, поля
  // ложились дважды. Правила edge-to-edge теперь перечисляют оба признака, а
  // card-правила отсекают потомков заливки.
  //
  // Прибитая шапка в цвет экрана (ветка stylepack/chrome-sticky-bg, решение
  // владельца 21.10.2026): СПЛОШНОЙ цвет экрана отдаётся переменной
  // `--screen-bg-fill`, ею красится `.qb-chrome-sticky` (public-paywall.css),
  // иначе шапка лежит белой полосой цвета пака поверх цветного экрана.
  // Градиент — не один цвет, переменной нет: шапка остаётся цветом пака.
  // Полноэкранная картинка поверх заливки сюда не доходит (fillInsideImageRoot).
  const solidFill = isSolidFill(layout.backgroundFill) ? layout.backgroundFill : undefined;
  return (
    <div
      style={
        solidFill
          ? ({ ...FULL_SCREEN_ROOT, '--screen-bg-fill': solidFill } as React.CSSProperties)
          : FULL_SCREEN_ROOT
      }
      data-testid="screen-bg-fill"
      data-bg-fill="edge"
    >
      {fillLayer}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          ...(layout.fillIsDark ? LIGHT_TEXT_PIN : {}),
          ...FULL_SCREEN_CONTENT_BOX,
        }}
        data-testid="screen-bg-fill-content"
      >
        {imageTree}
      </div>
    </div>
  );
}
